<template>

  <main>
    <section class="hero">
      <div class="hero-kicker">{{ $t('homePage.heroKicker') }}</div>
      <h1>{{ $t('homePage.heroTitle') }}</h1>
      <p>{{ $t('homePage.heroSub') }}</p>
      <div class="trust-row">
        <span>{{ $t('homePage.trust1') }}</span>
        <span>{{ $t('homePage.trust2') }}</span>
        <span>{{ $t('homePage.trust3') }}</span>
      </div>
    </section>

    <div class="section-head">
      <h2>{{ $t('homePage.products') }}</h2>
      <span v-if="maxArp" class="max-apy">APY {{ maxArp }}%</span>
    </div>

    <div class="empty" v-if="!loading && (!data || !data.length)">{{ $t('homePage.empty') }}</div>

    <div class="investment-list">
      <div v-for="(item, index) in data" :key="index" @click="ToView(item)" class="investment-item" :class="'tier-' + ((index % 4) + 1)" role="button" tabindex="0">
        <div class="card-shine"></div>
        <div class="card-top">
          <div>
            <div class="tier">{{ roman(index) }}</div>
            <h3 class="investment-title">{{item.name}}</h3>
          </div>
          <div class="apy">
            <span class="apy-num">{{item.arp}}%</span>
            <span class="apy-label">{{$t('homePage.arp')}}</span>
          </div>
        </div>
        <svg class="spark" viewBox="0 0 160 36" preserveAspectRatio="none" aria-hidden="true">
          <path :d="sparkPath(index)" fill="none" stroke="currentColor" stroke-width="1.4"/>
        </svg>
        <div class="investment-detail">
          <div class="info">
            <span class="info-label">{{$t('homePage.minimum')}}</span>
            <span class="info-value">{{item.start_funds}} USDT</span>
          </div>
          <div class="info">
            <span class="info-label">{{$t('homePage.holdingPeriod')}}</span>
            <span class="info-value">{{item.cycle}} {{$t('homePage.day')}}</span>
          </div>
        </div>
        <div class="card-cta">{{ $t('homePage.investNow') }}</div>
      </div>
    </div>

    <section class="faq" aria-labelledby="faq-title">
      <h2 id="faq-title">{{ $t('faq.title') }}</h2>
      <details v-for="n in 4" :key="n">
        <summary>{{ $t('faq.q' + n) }}</summary>
        <p>{{ $t('faq.a' + n) }}</p>
      </details>
    </section>
  </main>

  <div v-if="loading" class="loading-overlay" >
    <div class="loading-spinner"></div>
  </div>
</template>

<script>
import axios from "axios";
import config from '../config';
import {getCookie} from "@/utils/cookie";
import { applySeo, homeJsonLd } from "@/utils/seo";
export default {
  name: 'HomePage',
  data() {
    return {
      loading: true,
      data: null,
    };
  },
  created() {
    this.fetchData();
    const faqItems = [1, 2, 3, 4].map((n) => ({
      q: this.$t('faq.q' + n),
      a: this.$t('faq.a' + n),
    }));
    applySeo({
      title: this.$t('seo.home.title'),
      description: this.$t('seo.home.description'),
      path: '/',
      jsonLd: homeJsonLd(faqItems),
    });
  },
  computed: {
    maxArp() {
      if (!this.data || !this.data.length) return 0;
      return Math.max(...this.data.map(i => parseFloat(i.arp) || 0));
    }
  },
  methods: {
    roman(index) {
      return ['I', 'II', 'III', 'IV', 'V', 'VI'][index] || (index + 1);
    },
    sparkPath(index) {
      const seeds = [
        'M0 28 C20 26, 28 18, 42 16 S70 22, 88 12 S120 8, 160 6',
        'M0 24 C18 22, 36 20, 50 14 S80 18, 100 10 S130 12, 160 4',
        'M0 30 C22 20, 40 24, 58 12 S90 16, 110 8 S140 10, 160 3',
        'M0 26 C16 24, 34 12, 52 14 S84 6, 108 10 S136 2, 160 5',
      ];
      return seeds[index % seeds.length];
    },
    ToView(item){
      this.$router.push({ name: "productDetail", params: { id: item.id,name: item.name,arp: item.arp,start_funds: item.start_funds,cycle: item.cycle,describe: item.describe,team_arp: item.team_arp} });
    },
    async fetchData() {
      try {
        const response = await axios.get(config.apiHost+'/product_list',{
          headers: {
            'Content-Type': 'application/json',
            'loginsign':getCookie("signature"),
            'Loginmsg':getCookie("login_msg"),
          }
        });
        this.data = response.data;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.hero {
  position: relative;
  padding: 22px 20px 20px;
  margin-bottom: 22px;
  border-radius: 22px;
  overflow: hidden;
  background:
    linear-gradient(145deg, rgba(212, 175, 55, 0.16), rgba(12, 18, 32, 0.9) 42%, #0b101a),
    radial-gradient(400px 180px at 90% 0%, rgba(62, 224, 160, 0.12), transparent 60%);
  border: 1px solid rgba(212, 175, 55, 0.28);
}

.hero::after {
  content: "";
  position: absolute;
  right: -20px;
  top: -30px;
  width: 180px;
  height: 180px;
  border: 1px solid rgba(212, 175, 55, 0.18);
  border-radius: 50%;
  pointer-events: none;
}

.hero-kicker {
  color: #f0d78c;
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  margin-bottom: 10px;
}

.hero h1 {
  margin: 0 0 10px;
  font-family: "Cormorant Garamond", serif;
  font-size: 2.05rem;
  font-weight: 600;
  line-height: 1.15;
}

.hero p {
  margin: 0 0 16px;
  color: #b7bfce;
  font-size: 0.92rem;
  line-height: 1.6;
}

.trust-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.trust-row span {
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(212, 175, 55, 0.22);
  color: #e8d9a4;
  background: rgba(0,0,0,0.2);
}

.section-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin: 4px 4px 14px;
}

.section-head h2 {
  margin: 0;
  font-size: 0.95rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #c9d0dc;
  font-weight: 500;
}

.max-apy {
  color: #3ee0a0;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}

.empty {
  text-align: center;
  color: #9aa3b5;
  padding: 40px 0;
}

.investment-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-bottom: 1rem;
}

.investment-item {
  position: relative;
  overflow: hidden;
  background: linear-gradient(180deg, rgba(22, 29, 44, 0.95), rgba(12, 16, 26, 0.95));
  padding: 18px 18px 16px;
  border-radius: 18px;
  color: white;
  border: 1px solid rgba(212, 175, 55, 0.16);
  box-shadow: 0 16px 40px rgba(0,0,0,0.28);
  cursor: pointer;
  transition: transform 0.18s ease, border-color 0.18s ease;
}

.investment-item:active {
  transform: scale(0.985);
}

.tier-1 { border-color: rgba(212, 175, 55, 0.22); }
.tier-2 { border-color: rgba(140, 180, 255, 0.22); }
.tier-3 { border-color: rgba(62, 224, 160, 0.22); }
.tier-4 { border-color: rgba(240, 160, 90, 0.28); }

.card-shine {
  position: absolute;
  inset: 0;
  background: linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.04) 50%, transparent 60%);
  pointer-events: none;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.tier {
  font-size: 0.68rem;
  letter-spacing: 0.28em;
  color: #d4af37;
  margin-bottom: 6px;
}

.investment-title {
  font-size: 1.12rem;
  font-weight: 600;
  margin: 0;
}

.faq {
  margin: 8px 0 1.5rem;
  padding: 8px 4px 0;
}

.faq h2 {
  margin: 0 0 12px;
  font-size: 0.95rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #c9d0dc;
  font-weight: 500;
}

.faq details {
  border: 1px solid rgba(212, 175, 55, 0.14);
  background: rgba(16, 22, 35, 0.86);
  border-radius: 14px;
  padding: 12px 14px;
  margin-bottom: 8px;
}

.faq summary {
  cursor: pointer;
  font-weight: 600;
  color: #f0d78c;
}

.faq p {
  margin: 10px 0 0;
  color: #b7bfce;
  line-height: 1.65;
  font-size: 0.9rem;
}

.apy {
  text-align: right;
}

.apy-num {
  display: block;
  font-family: "Cormorant Garamond", serif;
  font-size: 2rem;
  line-height: 1;
  color: #3ee0a0;
  font-weight: 700;
}

.apy-label {
  font-size: 0.7rem;
  color: #8d96a8;
  letter-spacing: 0.12em;
}

.spark {
  width: 100%;
  height: 34px;
  margin: 8px 0 12px;
  color: rgba(212, 175, 55, 0.55);
}

.investment-detail {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding-top: 4px;
  border-top: 1px solid rgba(255,255,255,0.06);
}

.info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  font-size: 0.72rem;
  color: #8d96a8;
}

.info-value {
  font-size: 0.95rem;
  color: #f4f1ea;
  font-variant-numeric: tabular-nums;
}

.card-cta {
  margin-top: 14px;
  text-align: center;
  padding: 10px;
  border-radius: 999px;
  background: linear-gradient(180deg, #f3d98a 0%, #c9a227 100%);
  color: #1a1404;
  font-weight: 700;
  letter-spacing: 0.08em;
  font-size: 0.88rem;
}
</style>
